import notification from "antd/es/notification";
import browserLocale from "browser-locale";
import { differenceInCalendarDays } from "date-fns";
import { Task } from "redux-saga";
import { all, call, fork, join, put, race, select, take, takeLeading } from "redux-saga/effects";

import { OrganizationSetting } from "@mapmycustomers/shared";
import DistanceUnit from "@mapmycustomers/shared/enum/DistanceUnit";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import OrganizationFeature from "@mapmycustomers/shared/enum/OrganizationFeature";
import {
  EntityType,
  EntityTypesSupportingFieldCustomization,
  MapEntity,
} from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import Organization, { OrganizationMetaData } from "@mapmycustomers/shared/types/Organization";
import PersistentLastUsedLayoutData from "@mapmycustomers/shared/types/persistent/PersistentLastUsedLayoutData";
import { SandboxAccessTokenData } from "@mapmycustomers/shared/types/Sandbox";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";
import Setting from "@mapmycustomers/shared/types/Setting";
import { TokenHandoffCodeData } from "@mapmycustomers/shared/types/TokenHandoff";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import UserSetting from "@app/enum/UserSetting";
import { setUserIds } from "@app/scene/reports/store/actions";
import { getUserIds } from "@app/scene/reports/store/selectors";
import reduxStore from "@app/store";
import { fetchAnnouncements, suppressAnnouncements } from "@app/store/announcements/actions";
import { fetchBoundaryLegends } from "@app/store/boundaryLegends/actions";
import { clearChatConversation } from "@app/store/chat";
import { initializeCumul } from "@app/store/cumul/actions";
import { fetchAllCustomFields } from "@app/store/customFields/actions";
import { handleError } from "@app/store/errors/actions";
import { fetchAllGroups } from "@app/store/groups/actions";
import {
  areMileageTrackingAmountsVisible,
  doesOrganizationSupportMultiContactActivities,
  doesOrganizationSupportTerritories,
  getDefaultColumns,
  getFeatures,
  getLastUsedLayout,
  getMe,
  getOrganizationId,
  getOrganizationSettingValue,
  getUserSetting,
  getUserSettingValue,
  isCumulEnabled,
  isCurrentUserMember,
  isCurrentUserOwner,
} from "@app/store/iam";
import {
  fetchGeocodingLimit,
  fetchMe,
  fetchOrgSandbox,
  fetchPermissions,
  fetchPlans,
  findMe,
  getUserCountry,
  setOrganizationSize,
  updateSetting,
} from "@app/store/iam/actions";
import { isManager, isOwner } from "@app/store/iam/util";
import { fetchAllLayouts } from "@app/store/layout/actions";
import { changeLocale } from "@app/store/locale/actions";
import { getUsers } from "@app/store/members";
import { fetchRoles, fetchTeams, fetchUsers } from "@app/store/members/actions";
import { fetchNotificationsUnreadTotal } from "@app/store/notification/actions";
import { initializeNylas } from "@app/store/nylas/actions";
import { refreshGuidesStatus } from "@app/store/onboarding/actions";
import { fetchPinLegends } from "@app/store/pinLegends/actions";
import { fetchReferenceData } from "@app/store/referenceData/actions";
import { fetchAllSavedFilters } from "@app/store/savedFilters/actions";
import { fetchAllSchemas } from "@app/store/schema/actions";
import Iam from "@app/types/Iam";
import analyticsService from "@app/util/analytic/AnalyticsService";
import announcementsSettings from "@app/util/announcements/announcementsSettings";
import { getLocalTimeZone } from "@app/util/dates";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import companyFieldModel from "@app/util/fieldModel/CompanyFieldModel";
import dealFieldModel from "@app/util/fieldModel/DealFieldModel";
import getFieldModelByEntityType, {
  EntityTypeWithFieldModel,
} from "@app/util/fieldModel/getByEntityType";
import personFieldModel from "@app/util/fieldModel/PersonFieldModel";
import tripFieldModel from "@app/util/fieldModel/tripFieldModel";
import setupSourceObjectField from "@app/util/fieldModel/util/setupSourceObjectField";
import tripAmountsFieldFilter from "@app/util/fieldModel/util/tripAmountsFieldFilter";
import isValidDate from "@app/util/isValidDate";
import {
  activityLayoutModel,
  companyLayoutModel,
  dealLayoutModel,
  personLayoutModel,
} from "@app/util/layout/impl";
import loggingService from "@app/util/logging";
import { ensureGoogleMapsApiIsLoaded } from "@app/util/mapping";
import { parseApiDate } from "@app/util/parsers";
import getNotificationNodeWithAction from "@app/util/ui/getNotificationNodeWithAction";

import { fetchActivityTypes } from "../activity/actions";
import { callApi } from "../api/callApi";
import { fetchAllFunnels } from "../deal/actions";
import { fetchAllFileTags } from "../fileTags/actions";
import { buildRecordsPreviewConfiguration } from "../recordPreview/sagas";
import { getEntitySchemaFields } from "../schema";
import { initializeUserColors } from "../userColor/actions";

import { fetchSandboxAccessToken, fetchTokenHandoffCode, initializeApp } from "./actions";

const colorFieldFilter = (field: IField): boolean => !field.hasFeature(FieldFeature.COLOR_FIELD);
const territoryFieldFilter = (field: IField): boolean =>
  !field.hasFeature(FieldFeature.TERRITORY_FIELD);
const singleContactFieldFilter = (field: IField): boolean =>
  !field.hasFeature(FieldFeature.SINGLE_CONTACT_ONLY);
const multiContactFieldFilter = (field: IField): boolean =>
  !field.hasFeature(FieldFeature.MULTI_CONTACT_ONLY);

const languageToRegion: Record<string, string> = {
  en: "en-US",
  es: "es-ES",
};

export function* onInitialize() {
  try {
    // since we added code which requires google api into the navbar (starting from
    // create company modal), we need to load google apis from the beginning.
    // We only wait for the result of this action in the very end of this saga.
    const googleMapsLoadingTask: Task = yield fork(ensureGoogleMapsApiIsLoaded);

    // call fetch* actions
    yield put(fetchMe.request());
    yield put(findMe.request()); // not waiting for this one to complete or fail

    // now wait till all succeed or any fails, whichever happens first
    const { failure } = yield race({
      failure: take([fetchMe.failure]),
      success: all([take(fetchMe.success)]),
    });

    // fail app initialization if any of fetch actions failed, and succeed otherwise
    if (failure) {
      yield put(initializeApp.failure());
      yield put(handleError({ error: failure.payload }));
      return;
    }

    const me: Iam = yield select(getMe);

    let features: OrganizationMetaData["features"] = yield select(getFeatures);
    // patch features metadata if local overrides are specified
    // overrides are useful for dev testing because you can adjust available features
    // without actually modifying org data
    const hasFeaturesOverride: boolean = yield call(localSettings.hasFeaturesOverride);
    if (hasFeaturesOverride) {
      const overriddenFeatures: OrganizationMetaData["features"] = yield call(
        localSettings.getFeaturesOverride
      );
      features = { ...features, ...overriddenFeatures };
      yield put(
        fetchMe.success({
          ...me,
          organization: {
            ...me.organization,
            metaData: { ...me.organization.metaData, features },
          },
        })
      );
    }

    if (features?.[OrganizationFeature.DISALLOW_COLOR]?.enabled) {
      companyFieldModel.addFieldFilter(colorFieldFilter);
      personFieldModel.addFieldFilter(colorFieldFilter);
    }

    const territoriesEnabled: boolean = yield select(doesOrganizationSupportTerritories);
    if (!territoriesEnabled) {
      companyFieldModel.addFieldFilter(territoryFieldFilter);
      personFieldModel.addFieldFilter(territoryFieldFilter);
      dealFieldModel.addFieldFilter(territoryFieldFilter);
    }

    setupSourceObjectField(companyFieldModel, EntityType.COMPANY, me.organization.sourceObjects);
    setupSourceObjectField(personFieldModel, EntityType.PERSON, me.organization.sourceObjects);

    const isMultiContactEnabled: boolean = yield select(
      doesOrganizationSupportMultiContactActivities
    );
    activityFieldModel.addFieldFilter(
      isMultiContactEnabled ? singleContactFieldFilter : multiContactFieldFilter
    );

    const mileageTrackingValuesVisible: boolean = yield select(areMileageTrackingAmountsVisible);
    if (!mileageTrackingValuesVisible) {
      tripFieldModel.addFieldFilter(tripAmountsFieldFilter);
    }

    const userSettingLanguage: Setting | undefined = yield select(getUserSetting, "language");
    const languageSettingValue: null | string = userSettingLanguage?.value ?? null;
    const locale: string = languageSettingValue || (yield call(browserLocale));
    yield put(changeLocale.request(locale));
    yield race([take(changeLocale.success), take(changeLocale.failure)]);

    // Update "region" user setting to keep it in sync with the actual region user uses
    // Especially useful when Language is set to "auto-detect"
    const userSettingRegion: Setting | undefined = yield select(getUserSetting, "region");
    if (userSettingRegion && !localSettings.wasSkeletonKeyUsed()) {
      const region =
        (languageSettingValue ? languageToRegion[languageSettingValue] : undefined) ??
        (yield call(browserLocale));
      if (region !== userSettingRegion.value) {
        loggingService.debug(
          `Update region setting to "${region}" (previous value: "${userSettingRegion.value}")`
        );
        const setting = { ...userSettingRegion, value: region };
        yield put(updateSetting.request({ orgId: me.organization.id, setting, userId: me.id }));
      }
    }

    const userSettingTimezone: Setting | undefined = yield select(getUserSetting, "timezone");
    if (userSettingTimezone && !localSettings.wasSkeletonKeyUsed()) {
      const timezone = getLocalTimeZone();
      if (timezone && timezone !== userSettingTimezone.value) {
        const setting = { ...userSettingTimezone, value: timezone };
        yield put(updateSetting.request({ orgId: me.organization.id, setting, userId: me.id }));
      }
    }

    yield put(initializeUserColors());

    yield call(analyticsService.identify, {
      ...me,
      "sign-upSource": localSettings.getSignupCompleted() ? "Web" : undefined,
    });
    yield call(analyticsService.group, me);
    localSettings.setSignupCompleted(false);

    const distanceUnit: DistanceUnit =
      (yield select(getUserSettingValue, UserSetting.DISTANCE_UNIT)) ??
      (yield select(getOrganizationSettingValue, OrganizationSetting.DISTANCE_UNIT)) ??
      DistanceUnit.MILE;
    localSettings.setSiUnitsUsage(distanceUnit === DistanceUnit.KM);

    const currency: string =
      (yield select(getOrganizationSettingValue, OrganizationSetting.CURRENCY)) ?? "USD";
    localSettings.setCurrencyCode(currency);

    const uidExport: boolean =
      (yield select(getOrganizationSettingValue, OrganizationSetting.EXPORT_UID)) ?? false;
    localSettings.setUidExportSetting(uidExport);

    const prevUserId = localSettings.getUserId();
    // means that another user logged in, from this, we should clear view settings
    if (prevUserId !== me.id) {
      localSettings.resetViewSettings();
      // The chat thread lives in sessionStorage, and signOut is a same-tab navigation, which
      // does not clear it — so without this, the next rep to log in on this tab inherits the
      // previous rep's conversation.
      yield put(clearChatConversation());
      localSettings.setUserId(me.id);
    }

    // call fetch* actions which required having IAM data in the store
    yield put(fetchPermissions.request(me.role.id));
    yield put(fetchAllCustomFields.request());
    yield put(fetchAllGroups.request());
    yield put(fetchAllLayouts.request());
    yield put(fetchAllSchemas.request());
    yield put(fetchAllFileTags.request());
    yield put(fetchRoles.request());
    yield put(fetchTeams.request());
    yield put(fetchUsers.request());
    yield put(fetchAllFunnels.request());
    yield put(fetchActivityTypes.request());
    // these two don't really require IAM data, but would fail if token is outdated, so let's call them
    // after having IAM to guarantee we use a fresh token
    yield put(fetchReferenceData.request());
    yield put(initializeNylas.request());
    yield put(fetchGeocodingLimit.request());
    yield put(fetchPinLegends.request());
    yield put(refreshGuidesStatus.request());
    // Not in the race below: auto-running guides wait for it, the app never does.
    // A tab an announcement CTA opened fetches nothing and lets the guides start at once.
    const announcementsOff: boolean = yield call(announcementsSettings.areAnnouncementsOffInTab);
    yield put(announcementsOff ? suppressAnnouncements() : fetchAnnouncements.request());
    // need to load them in order to initialize mapViewState correctly
    // See getMapViewSettings function for details.
    yield put(fetchBoundaryLegends.request());

    // now wait till all succeed or any fails, whichever happens first
    const { failure2 } = yield race({
      failure2: take([
        fetchPermissions.failure,
        fetchAllCustomFields.failure,
        fetchAllGroups.failure,
        fetchAllSchemas.failure,
        fetchRoles.failure,
        fetchTeams.failure,
        fetchUsers.failure,
        fetchAllFunnels.failure,
        fetchActivityTypes.failure,
        fetchReferenceData.failure,
        initializeNylas.failure,
        fetchGeocodingLimit.failure,
        fetchPinLegends.failure,
        refreshGuidesStatus.failure,
        fetchBoundaryLegends.failure,
      ]),
      success2: all([
        take(fetchPermissions.success),
        take(fetchAllCustomFields.success),
        take(fetchAllGroups.success),
        take(fetchAllSchemas.success),
        take(fetchRoles.success),
        take(fetchTeams.success),
        take(fetchUsers.success),
        take(fetchAllFunnels.success),
        take(fetchActivityTypes.success),
        take(fetchReferenceData.success),
        take(initializeNylas.success),
        take(fetchGeocodingLimit.success),
        take(fetchPinLegends.success),
        take(refreshGuidesStatus.success),
        take(fetchBoundaryLegends.success),
      ]),
    });

    // fail app initialization if any of fetch actions failed, and succeed otherwise
    if (failure2) {
      yield put(initializeApp.failure());
      yield put(handleError({ error: failure2.payload }));
      return;
    }

    // We need to setSchema here rather than in fetchAllSchemas saga to ensure
    // both schemas and custom fields are loaded.
    for (const entityType of [
      EntityType.COMPANY,
      EntityType.PERSON,
      EntityType.DEAL,
      EntityType.ACTIVITY,
    ] as EntityTypesSupportingFieldCustomization[]) {
      const fieldModel: IFieldModel = getFieldModelByEntityType(
        entityType as EntityTypeWithFieldModel
      );
      const schema: SchemaField[] = yield select(getEntitySchemaFields, entityType);
      fieldModel.setSchema(schema);
    }

    // initialize reports pages with users selection
    const allUsers: User[] = yield select(getUsers);
    const reportUserIds: undefined | User["id"][] = yield select(getUserIds);
    if (!reportUserIds?.length) {
      const currentUserMember: boolean = yield select(isCurrentUserMember);
      if (currentUserMember) {
        yield put(setUserIds([me.id]));
      } else {
        const currentUserOwner: boolean = yield select(isCurrentUserOwner);
        yield put(
          setUserIds(
            allUsers.filter((user) => (isOwner(user) ? currentUserOwner : true)).map(({ id }) => id)
          )
        );
      }
    }

    // initialize layouts
    const recentLayouts: PersistentLastUsedLayoutData | undefined = yield select(getLastUsedLayout);
    if (recentLayouts?.[EntityType.ACTIVITY]) {
      activityLayoutModel.setRecentLayoutId(recentLayouts[EntityType.ACTIVITY]!);
    }
    if (recentLayouts?.[EntityType.COMPANY]) {
      companyLayoutModel.setRecentLayoutId(recentLayouts[EntityType.COMPANY]!);
    }
    if (recentLayouts?.[EntityType.DEAL]) {
      dealLayoutModel.setRecentLayoutId(recentLayouts[EntityType.DEAL]!);
    }
    if (recentLayouts?.[EntityType.PERSON]) {
      personLayoutModel.setRecentLayoutId(recentLayouts[EntityType.PERSON]!);
    }

    // setting default columns, should only be done after CFs are fetched
    const defaultColumns: Exclude<OrganizationMetaData["defaultColumns"], undefined> =
      yield select(getDefaultColumns);
    Object.keys(defaultColumns).forEach((entityType) => {
      const fieldModel = getFieldModelByEntityType(entityType as EntityTypeWithFieldModel);
      if (!fieldModel) {
        loggingService.debug("Invalid entity type in default columns", entityType);
        return;
      }

      const defaultFields =
        defaultColumns[
          entityType as keyof Exclude<OrganizationMetaData["defaultColumns"], undefined>
        ]
          ?.map((platformFieldName) => fieldModel.getByPlatformName(platformFieldName))
          .filter(isDefined)
          .map((field) => field.name) ?? [];

      if (defaultFields.length) {
        fieldModel.fields.forEach((field) => {
          field.toggleFeature(
            FieldFeature.VISIBLE_BY_DEFAULT,
            field.hasFeature(FieldFeature.ALWAYS_VISIBLE) || defaultFields.includes(field.name)
          );
        });
      }
    });

    // owners and managers are allowed to see Verified and CheckInDistance fields and filter by them
    if (isOwner(me) || isManager(me)) {
      const verifiedField = activityFieldModel.getByName(ActivityFieldName.RELIABILITY);
      verifiedField?.toggleFeature(FieldFeature.NON_LIST_VIEW, false);
      const checkInDistanceField = activityFieldModel.getByName(
        ActivityFieldName.CHECK_IN_DISTANCE
      );
      checkInDistanceField?.toggleFeature(FieldFeature.NON_LIST_VIEW, false);
    }

    // Only fetch saved filters when schemas and custom fields are fetched
    yield put(fetchAllSavedFilters.request());

    // wait till all succeed or any fails, whichever happens first
    const { failure3 } = yield race({
      failure3: take([fetchAllSavedFilters.failure]),
      success3: all([take(fetchAllSavedFilters.success)]),
    });

    // fail app initialization if any of fetch actions failed, and succeed otherwise
    if (failure3) {
      yield put(initializeApp.failure());
      yield put(handleError({ error: failure3.payload }));
      return;
    }

    // https://mapmycustomers.atlassian.net/browse/TART-22746
    // https://mapmycustomers.atlassian.net/browse/TART-25493
    if (me.organization.id === 31104 || me.organization.id === 33062) {
      yield put(setOrganizationSize("medium"));
    } else {
      const pinsResponse: ListResponse<MapEntity> = yield callApi("fetchPins", me.organization.id, {
        $filters: { entities: { accounts: {}, contacts: {}, deals: {} } },
        $limit: 0,
      });
      // a threshold after which we consider organization big
      if (pinsResponse.accessible > 12000) {
        yield put(setOrganizationSize("big"));
      }
    }

    const isCumulEnabledForOrg: boolean = yield select(isCumulEnabled);

    // Initializing cumul here in order to get cumul auth response and
    // dashboard list in advance to redirect reports directly to first pinned dashboard
    // and to show pinned dashboards name in menu which will be fetched from dashboard's list
    if (isCumulEnabledForOrg) {
      yield put(initializeCumul.request());
    }

    // now let's ensure google maps loading task (which was executed in parallel) is also complete
    yield join(googleMapsLoadingTask);

    yield put(initializeApp.success(me));

    // not waiting for this request to complete
    yield put(fetchNotificationsUnreadTotal.request());

    // not waiting for this one to complete or fail
    yield put(fetchOrgSandbox.request());

    // not waiting for this one to complete or fail
    yield put(getUserCountry.request());

    // not waiting for this one to complete or fail
    yield put(fetchPlans.request());

    // Let's check if password expires soon
    const passwordExpirationDate = parseApiDate(me.passwordExpiresAt);
    const expiresInDays = differenceInCalendarDays(passwordExpirationDate, Date.now());
    if (isValidDate(passwordExpirationDate) && expiresInDays <= 30 && !me.ssoLogin) {
      const intl = i18nService.getIntl();
      if (intl) {
        notification.warning({
          duration: 0, // never hide automatically
          key: "passwordExpirationWarning",
          message: getNotificationNodeWithAction(
            intl.formatMessage(
              {
                id: "app.auth.passwordExpirationWarning",
                defaultMessage:
                  "Your password is expiring in {count} {count, plural, one {day} other {days}}",
                description: "A message of the password expiration warning",
              },
              {
                count: expiresInDays,
              }
            ),
            intl.formatMessage({
              id: "app.auth.passwordExpirationWarning.action",
              defaultMessage: "Update Now",
              description: "An action of the password expiration warning",
            }),
            () => {
              notification.close("passwordExpirationWarning");
              reduxStore.history.push(
                `${Path.SETTINGS}/${SettingPath.PERSONAL_PROFILE}?changePassword`
              );
            }
          ),
        });
      }
    }

    // initialize record preview configuration
    yield call(buildRecordsPreviewConfiguration);
  } catch (error) {
    yield put(initializeApp.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchSandboxAccessToken({
  payload: { callback },
}: ReturnType<typeof fetchSandboxAccessToken.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { accessToken, code }: SandboxAccessTokenData = yield callApi(
      "getSandboxAccessToken",
      orgId
    );
    if (callback) {
      yield call(callback, accessToken, code);
    }
    yield put(fetchSandboxAccessToken.success(accessToken));
  } catch (error) {
    yield put(fetchSandboxAccessToken.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchTokenHandoffCode({
  payload: { callback, failureCallback },
}: ReturnType<typeof fetchTokenHandoffCode>) {
  try {
    const { code }: TokenHandoffCodeData = yield callApi("createTokenHandoffCode");
    yield call(callback, code);
  } catch (error) {
    // log but no toast: the caller falls back to the token URL, so the mint
    // failure needs observability without a message the user can't act on
    yield put(handleError({ displayNotification: false, error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

export function* appSaga() {
  yield takeLeading(initializeApp.request, onInitialize);
  yield takeLeading(fetchSandboxAccessToken.request, onFetchSandboxAccessToken);
  yield takeLeading(fetchTokenHandoffCode, onFetchTokenHandoffCode);
}
